import { ReactNode, useEffect } from 'react';
import { PatientClinicContext } from './PatientClinicContext';
import { dtpPath, useDtpParams, patientHomePath } from '../../lib/clinicSite';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Package, Store } from 'lucide-react';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { useDtpCartStore } from '../../store/useDtpCartStore';
import { useDtpAutoImport } from '../../hooks/useDtpAutoImport';
import { usePatientImpersonationHandoff } from '../../hooks/usePatientImpersonation';
import { useStorefrontBranding, useStorefrontPageIdentity, useStorefrontFonts } from '../../hooks/useStorefrontBranding';
import { PatientNavigation, PatientAccountMenu } from './PatientNavigation';
import { resolveStorefrontTheme } from '../../lib/storefrontTheme';
import { endPatientSupportChat, startPatientSupportChat } from '../../lib/patientSupportChat';
import { SupportLauncher } from './PatientSupport';
import { track } from '../../lib/analytics';

export function DtpLayout({ children, membershipEnabled: clinicMembershipEnabled }: { children?: ReactNode; membershipEnabled?: boolean } = {}) {
  const { physicianId } = useDtpParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isPatientPage = /\/(home|portal|account|orders|results|membership|billing|messages|treatments|refills|support|assistant)(?:\/|$)/.test(pathname);
  const branding = useStorefrontBranding(physicianId);
  useStorefrontPageIdentity(branding?.name, branding?.theme);
  const membershipEnabled = clinicMembershipEnabled ?? (branding?.membershipEnabled === true);
  const themed = !!branding?.theme?.presetId;
  const theme = resolveStorefrontTheme(branding?.theme);
  useStorefrontFonts(themed ? theme.googleFontsHref : null);
  const { isAuthenticated, patient, logout, impersonated } = useDtpAuthStore();
  const itemCount = useDtpCartStore(s => s.items.length);
  // Honor an HMAC-signed auto-import link if one lands on any store page.
  useDtpAutoImport(physicianId);
  // Spend a `?pi=` handoff code from the clinic portal, on any store page.
  const { error: handoffError } = usePatientImpersonationHandoff();

  // Support chat follows the patient session; a clinic viewing the portal never gets one.
  const supportSession = isAuthenticated && !impersonated && patient?._id ? `${physicianId}:${patient._id}` : null;
  useEffect(() => {
    if (supportSession) startPatientSupportChat(supportSession);
    else endPatientSupportChat();
  }, [supportSession]);

  const handleLogout = () => {
    endPatientSupportChat();
    logout();
    navigate(dtpPath(physicianId));
  };

  return (
    <PatientClinicContext.Provider value={{ membershipEnabled }}>
    <div className={`min-h-screen bg-gray-50 flex flex-col ${themed ? 'store-branded' : ''} ${isPatientPage ? 'patient-portal' : ''}`} style={themed ? theme.cssVars as React.CSSProperties : undefined}>
      {themed && <style>{`.store-branded { background:var(--store-bg); color:var(--store-text); font-family:var(--font-body); } .store-branded .bg-blue-600, .store-branded .hover\\:bg-blue-700:hover { background-color:var(--store-accent); color:var(--store-accent-fg); } .store-branded h1, .store-branded h2 { font-family:var(--font-heading); }`}</style>}
      {/* Staff and clinics reach this portal to see what a patient sees. Say so
          on every page: an operator must never mistake it for their own
          account, and a patient looking on should know what is happening. */}
      {/* Say why, rather than bouncing to the storefront with no explanation —
          an expired or already-spent link is otherwise indistinguishable from
          the feature being broken. */}
      {handoffError && (
        <div className="bg-red-600 text-white text-sm font-medium px-4 py-2 text-center" role="alert">
          {handoffError}
        </div>
      )}
      {impersonated && (
        <div className="bg-amber-500 text-amber-950 text-sm font-medium px-4 py-2 text-center">
          Viewing {patient?.firstName ? `${patient.firstName}'s` : 'this patient\u2019s'} portal on their behalf.
          Purchases and membership changes are disabled.
        </div>
      )}
      {/* Header */}
      {isPatientPage ? (
        // The portal header: clinic, page navigation (a bottom tab bar on
        // phones), then the way back to the shop, the cart and the account menu.
        <header className="sticky top-0 z-20 border-b border-[var(--portal-line)] bg-[color-mix(in_srgb,var(--portal-card-solid)_72%,transparent)] backdrop-blur-xl">
          <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-6 lg:px-8">
            <button onClick={() => navigate(dtpPath(physicianId))}
              className="portal-display flex min-w-0 shrink items-center gap-2 text-lg font-semibold text-[var(--portal-ink)] transition hover:opacity-80">
              {branding?.theme?.logoUrl ? <img src={branding.theme.logoUrl} alt={branding.name} className="h-9 w-[132px] object-contain object-left" /> : <><span aria-hidden="true" className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-[var(--portal-accent)] text-[var(--portal-accent-fg)]"><Package className="h-4 w-4" /></span><span className="truncate max-w-[150px] lg:max-w-[220px]">{branding?.name || 'Shop'}</span></>}
            </button>
            {isAuthenticated && <div className="hidden min-w-0 flex-1 justify-center lg:flex"><PatientNavigation membershipEnabled={membershipEnabled} /></div>}
            <div className="ml-auto flex items-center gap-1 lg:ml-0">
              <button onClick={() => navigate(dtpPath(physicianId))}
                className="hidden h-10 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-[var(--portal-muted)] transition hover:bg-[rgba(36,26,21,0.05)] hover:text-[var(--portal-ink)] sm:inline-flex lg:hidden xl:inline-flex">
                <Store aria-hidden="true" className="h-4 w-4" /> Shop
              </button>
              <button onClick={() => { track('cart_viewed', { itemCount }); navigate(dtpPath(physicianId, `/checkout`)); }} aria-label={itemCount ? `Cart, ${itemCount} item${itemCount === 1 ? '' : 's'}` : 'Cart'}
                className="relative grid h-10 w-10 place-items-center rounded-full text-[var(--portal-ink)] transition hover:bg-[rgba(36,26,21,0.05)]">
                <ShoppingCart aria-hidden="true" className="h-[18px] w-[18px]" />
                {itemCount > 0 && <span aria-hidden="true" className="absolute right-0.5 top-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[var(--portal-accent)] px-1 text-[10px] font-bold text-[var(--portal-accent-fg)]">{itemCount}</span>}
              </button>
              {isAuthenticated && <PatientAccountMenu firstName={patient?.firstName} lastName={patient?.lastName} onSignOut={handleLogout} membershipEnabled={membershipEnabled} />}
            </div>
          </div>
        </header>
      ) : (
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(dtpPath(physicianId))}
              className="flex min-w-0 items-center gap-2 text-lg font-semibold transition-colors text-gray-900 hover:text-blue-600"
            >
              {branding?.theme?.logoUrl ? <img src={branding.theme.logoUrl} alt={branding.name} className="h-10 w-[140px] sm:w-[200px] object-contain object-left" /> : <><Package className="w-6 h-6 shrink-0" /><span className="truncate max-w-[140px] sm:max-w-[200px]">{branding?.name || 'Shop'}</span></>}
            </button>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Outside the portal, the clinic bar offers the way into it. */}
              <button onClick={() => navigate(patientHomePath(physicianId))} className="min-h-[44px] text-sm text-gray-600 hover:text-gray-900">
                {isAuthenticated ? 'Patient portal' : 'Sign in'}
              </button>

              <button
                onClick={() => { track('cart_viewed', { itemCount }); navigate(dtpPath(physicianId, `/checkout`)); }}
                aria-label="Cart"
                className="relative min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 hover:text-gray-900"
              >
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>
      )}

      {/* Main content */}
      <main className={`flex-1 mx-auto w-full px-4 sm:px-6 lg:px-8 ${isPatientPage ? `max-w-6xl pb-12 pt-8 sm:pt-12` : 'max-w-5xl py-8'}`}>
        {children ?? <Outlet />}
      </main>
      {/* Phones: the same navigation as a tab bar pinned to the bottom. */}
      {isAuthenticated && isPatientPage && <SupportLauncher />}
      {isAuthenticated && isPatientPage && <div className="lg:hidden"><PatientNavigation membershipEnabled={membershipEnabled} tabBar /></div>}

      {/* Footer */}
      <footer className={isPatientPage ? `${isAuthenticated ? 'pb-28' : 'pb-10'} lg:pb-10` : 'bg-white border-t border-gray-200 py-6'}>
        <div className={`${isPatientPage ? 'max-w-6xl' : 'max-w-5xl'} mx-auto px-4 sm:px-6 lg:px-8 text-center ${isPatientPage ? 'text-xs text-[var(--portal-muted)]' : 'text-sm text-gray-500'}`}>
          Powered by Bloom Health
        </div>
      </footer>
    </div>
    </PatientClinicContext.Provider>
  );
}
